import React, { useEffect, useMemo, useState } from 'react'
import { TextInput } from 'core/components/form/text-input'
import { useForm } from 'core/hooks/form'
import { requiredMessage, PlateMask } from 'core/constants'
import { Field } from 'core/components/form/field'
import { ModalForm } from 'core/components/modal'
import { ErrorMessage } from 'core/components/form/error-message'
import { GetVehicleTypes } from './utils'
import { isOnlyNumbers } from 'core/helpers/validate'
import { applyMask } from 'core/helpers/mask'
import { Switch } from 'core/components/form/switch'
import { ModalConfirm } from 'core/components/modal/ModalConfirm'

export const VehicleForm = ({ show, route, refresh, service, global, vehicles }) => {
  const { operator } = global
  const plate = route.match.params.plate

  const form = useForm(
    {
      displayName: ent => ent.plate,
      initialEntity: {
        plate: '',
        description: '',
        vehicleTypeId: null,
        password: '',
        automaticDebit: false,
      },
      validate: values => {
        const errors = {}

        if (!values.plate) {
          errors.plate = requiredMessage
        }

        if (!values.description) {
          errors.description = requiredMessage
        }

        if (operator.user.isFleetOwner) {
          if (!values.password && !plate) {
            errors.password = requiredMessage
          } else if (values.password) {
            if (values.password.length < 6) {
              errors.password = 'A senha deve conter no mínimo 6 números.'
            } else if (values.password && !isOnlyNumbers(values.password)) {
              errors.password = 'A senha deve conter apenas números.'
            }
          }
        }

        return errors
      },
    },
    route,
  )

  const [modal, setModal] = useState({
    title: '',
    message: '',
    opened: false,
  })

  const vehicleTypes = useMemo(() => GetVehicleTypes(operator.parameters.vehicleTypes), [
    operator.parameters.vehicleTypes,
  ])

  const { entity, errors, touched } = form

  useEffect(() => {
    const vehicle = vehicles.find(vehicle => vehicle.plate === plate)
    form.setValues(prev => ({ ...prev, ...vehicle }))
    // eslint-disable-next-line
  }, [plate])

  const changeVehicle = data => {
    service
      .post('vehicle.change', data)
      .then(refresh)
      .then(() => {
        route.history.goBack()
        form.setSubmitting(false)
      })
      .catch(err => form.setErrors({ global: err.message }))
  }

  return (
    <ModalForm
      title={plate ? applyMask(PlateMask, plate) : 'Novo Veículo'}
      show={show}
      fetching={form.fetching}
      submitting={form.submitting}
      resetForm={form.resetForm}
      closeAction={route.history.goBack}
      onSubmit={() =>
        form.handleSubmit(data => {
          if (data.automaticDebit) {
            setModal(prev => ({
              ...prev,
              opened: true,
              title: 'Ativar Débito Automático',
              message: operator.parameters.automaticDebitTerms,
              onAct: confirmed => {
                if (confirmed) changeVehicle(data)
                else form.setSubmitting(false)
              },
            }))
          } else {
            changeVehicle(data)
          }
        })
      }
    >
      <Field label="Categoria">
        <div className="vehicle-type-modal">
          {vehicleTypes.map(ent => (
            <div
              className={entity.vehicleTypeId === ent.id ? 'item active' : 'item'}
              key={ent.id}
              onClick={() => form.setValues(prev => ({ ...prev, vehicleTypeId: ent.id }))}
            >
              <i className={ent.icon} />
              <span>{ent.name}</span>
            </div>
          ))}
        </div>
      </Field>

      <div className="row">
        <div className="col-lg">
          <Field label="Placa">
            <TextInput
              autoFocus
              customClassName="form-control-xl"
              mask={PlateMask}
              meta={{
                error: errors.plate,
                touched: touched.plate,
              }}
              onChange={(p, type) =>
                form.handleChange({
                  path: 'plate',
                  type,
                  values: { plate: p.toLocaleUpperCase() },
                })
              }
              value={entity.plate}
              disabled={!!plate}
            />
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-lg">
          <Field label="Marca/Modelo">
            <TextInput
              customClassName="form-control-xl"
              meta={{ error: errors.description, touched: touched.description }}
              onChange={(description, type) =>
                form.handleChange({ path: 'description', type, values: { description } })
              }
              value={entity.description}
            />
          </Field>
        </div>
      </div>

      {operator.user.isFleetOwner && (
        <div className="row">
          <div className="col-lg">
            <Field label="Senha">
              <TextInput
                customClassName="form-control-xl"
                meta={{ error: errors.password, touched: touched.password }}
                onChange={(password, type) =>
                  form.handleChange({ path: 'password', type, values: { password } })
                }
                value={entity.password}
                type="password"
              />
            </Field>
          </div>
        </div>
      )}

      {operator.parameters.allowAutomaticDebit && (
        <div className="row">
          <div className="col-lg">
            <Switch
              checked={entity.automaticDebit}
              id="automaticDebit"
              label="Débito automático"
              onChange={automaticDebit => form.setValues({ automaticDebit })}
            />
          </div>
        </div>
      )}

      <ErrorMessage error={errors.global} />

      <ModalConfirm modal={modal} setModal={setModal} />
    </ModalForm>
  )
}
